import { ActionIcon, Badge, Button, Group, Paper, ScrollArea, Table, Text, Title, Tooltip } from '@mantine/core';
import { DocumentDownload, Renew } from '@carbon/icons-react';
import { Loading } from '@carbon/react';
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { defineMessages, IntlShape, useIntl } from 'react-intl';
import { useGetMyInvoicesQuery } from 'api/billingEndpoints';
import { SubscriptionInvoiceDto } from 'api/dtos/billingDtos';
import ErrorActionState from 'Components/Common/ErrorState/ErrorActionState';
import { toDateFormat } from 'utils/timeDateUtils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getToken } from 'store/selectors/authSelectors';
import styles from './PaymentHistory.module.scss';

const formatAmount = (amount: number, currency: string) => new Intl.NumberFormat('pl-PL', {
    style: 'currency',
    currency: currency.toUpperCase(),
}).format(amount);

const formatInvoiceStatus = (status: string) => {
    const normalizedStatus = status.toLowerCase();

    if (normalizedStatus === 'paid') {
        return {
            label: 'opłacona',
            color: 'green',
        };
    }

    if (normalizedStatus === 'open') {
        return {
            label: 'DO OPŁACENIA',
            color: 'orange',
        };
    }

    return {
        label: normalizedStatus,
        color: 'gray',
    };
};

const shouldShowInvoice = (invoice: SubscriptionInvoiceDto) => (
    invoice.amountPaid > 0 || invoice.amountDue > 0
);

const isUnpaidInvoice = (invoice: SubscriptionInvoiceDto) => (
    invoice.status.toLowerCase() === 'open' &&
    (invoice.amountDue > 0 || invoice.canBePaid === true)
);

const getPaymentHistoryColumns = (
    intl: IntlShape,
    onDownloadInvoice: (invoice: SubscriptionInvoiceDto) => void,
): ColumnDef<SubscriptionInvoiceDto>[] => [
    {
        accessorKey: 'number',
        header: intl.formatMessage(messages.invoiceNumber),
        cell: ({ row }) => row.original.number ?? '-',
    },
    {
        accessorKey: 'paidAt',
        header: intl.formatMessage(messages.paymentDate),
        cell: ({ row }) => toDateFormat(row.original.paidAt ?? row.original.createdAt),
    },
    {
        accessorKey: 'amountPaid',
        header: intl.formatMessage(messages.amount),
        cell: ({ row }) => {
            if (row.original.status.toLowerCase() === 'open') {
                return intl.formatMessage(messages.checkInvoice);
            }

            return formatAmount(row.original.amountPaid, row.original.currency);
        },
    },
    {
        accessorKey: 'status',
        header: intl.formatMessage(messages.status),
        cell: ({ row }) => {
            const status = formatInvoiceStatus(row.original.status);

            return (
                <Badge color={status.color} variant="light">
                    {status.label}
                </Badge>
            );
        },
    },
    {
        accessorKey: 'invoicePdfUrl',
        header: intl.formatMessage(messages.invoice),
        cell: ({ row }) => {
            const isAvailable = Boolean(row.original.invoicePdfUrl || row.original.hostedInvoiceUrl);

            if (!isAvailable) {
                return '-';
            }

            return (
                <Group
                    gap={6}
                    wrap="nowrap"
                    className={styles.invoiceLink}
                    role="button"
                    tabIndex={0}
                    onClick={() => { onDownloadInvoice(row.original); }}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            onDownloadInvoice(row.original);
                        }
                    }}
                >
                    <ActionIcon
                        variant="subtle"
                        color="blue"
                        aria-label={intl.formatMessage(messages.downloadPdf)}
                    >
                        <DocumentDownload size={16} />
                    </ActionIcon>
                    <Text size="sm" fw={600}>
                        {intl.formatMessage(messages.downloadPdf)}
                    </Text>
                </Group>
            );
        },
    },
];

export const PaymentHistory = () => {
    const intl = useIntl();
    const { data: invoices = [], isFetching, isError, refetch } = useGetMyInvoicesQuery();
    const token = useSelector(getToken);
    const [isRefreshingSubscription, setIsRefreshingSubscription] = useState(false);
    const [isRefreshCoolingDown, setIsRefreshCoolingDown] = useState(false);
    const visibleInvoices = useMemo(
        () => invoices.filter(shouldShowInvoice),
        [invoices],
    );
    const hasUnpaidInvoice = useMemo(
        () => visibleInvoices.some(isUnpaidInvoice),
        [visibleInvoices],
    );
    const isRefreshDisabled = !token || !hasUnpaidInvoice || isRefreshingSubscription || isRefreshCoolingDown;
    const refreshTooltipMessage = (() => {
        if (!hasUnpaidInvoice) {
            return intl.formatMessage(messages.refreshDisabledTooltip);
        }

        if (isRefreshingSubscription || isRefreshCoolingDown) {
            return intl.formatMessage(messages.refreshCooldownTooltip);
        }

        return intl.formatMessage(messages.refreshTooltip);
    })();

    useEffect(() => {
        if (!isRefreshCoolingDown) {
            return undefined;
        }

        const timeoutId = window.setTimeout(() => {
            setIsRefreshCoolingDown(false);
        }, 1000);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [isRefreshCoolingDown]);

    const onDownloadInvoice = useCallback((invoice: SubscriptionInvoiceDto) => {
        const invoiceUrl = invoice.invoicePdfUrl || invoice.hostedInvoiceUrl;

        if (invoiceUrl) {
            window.open(invoiceUrl, '_blank', 'noopener,noreferrer');
        }
    }, []);

    const onRefreshSubscription = useCallback(async () => {
        if (isRefreshDisabled) {
            return;
        }

        setIsRefreshCoolingDown(true);
        setIsRefreshingSubscription(true);
        try {
            await fetch(`${process.env.REACT_APP_API_URL}/subscriptionplan/my-subscription/refresh`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
        } catch {
            // Keep the refresh isolated from the main RTK Query render cycle.
        } finally {
            setIsRefreshingSubscription(false);
        }
    }, [isRefreshDisabled, token]);

    const paymentHistoryColumns = useMemo(
        () => getPaymentHistoryColumns(
            intl,
            onDownloadInvoice,
        ),
        [intl, onDownloadInvoice],
    );

    const table = useReactTable({
        data: visibleInvoices,
        columns: paymentHistoryColumns,
        getCoreRowModel: getCoreRowModel(),
    });

    if (isFetching) {
        return <Loading />;
    }

    if (isError) {
        return <ErrorActionState onClick={refetch} />;
    }

    return (
        <Paper shadow="sm" p="xl" radius="md" withBorder>
            <Group justify="space-between" align="center" mb="md">
                <Title order={2} size="h3">
                    {intl.formatMessage(messages.paymentHistory)}
                </Title>
                <Tooltip label={refreshTooltipMessage} withArrow multiline w={320}>
                    <span className={styles.refreshButtonTooltipWrapper}>
                        <Button
                            size="sm"
                            variant="light"
                            leftSection={<Renew size={16} />}
                            disabled={isRefreshDisabled}
                            loading={isRefreshingSubscription}
                            onClick={onRefreshSubscription}
                        >
                            {intl.formatMessage(messages.refreshSubscription)}
                        </Button>
                    </span>
                </Tooltip>
            </Group>

            <ScrollArea className={styles.tableScroll}>
                <Table withRowBorders highlightOnHover className={styles.table}>
                    <Table.Thead>
                        {table.getHeaderGroups().map(headerGroup => (
                            <Table.Tr key={headerGroup.id}>
                                {headerGroup.headers.map(header => (
                                    <Table.Th key={header.id}>
                                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                                    </Table.Th>
                                ))}
                            </Table.Tr>
                        ))}
                    </Table.Thead>
                    <Table.Tbody>
                        {table.getRowModel().rows.map(row => (
                            <Table.Tr key={row.id}>
                                {row.getVisibleCells().map(cell => (
                                    <Table.Td key={cell.id}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </Table.Td>
                                ))}
                            </Table.Tr>
                        ))}
                        {table.getRowModel().rows.length === 0 && (
                            <Table.Tr>
                                <Table.Td colSpan={paymentHistoryColumns.length}>
                                    <Text ta="center" c="dimmed" py="lg">
                                        {intl.formatMessage(messages.emptyState)}
                                    </Text>
                                </Table.Td>
                            </Table.Tr>
                        )}
                    </Table.Tbody>
                </Table>
            </ScrollArea>
        </Paper>
    );
};

const messages = defineMessages({
    paymentHistory: {
        id: 'src.Screens.Dashboard.Subscription.PaymentHistory.paymentHistory',
        defaultMessage: 'Historia płatności',
    },
    refreshSubscription: {
        id: 'src.Screens.Dashboard.Subscription.PaymentHistory.refreshSubscription',
        defaultMessage: 'Odśwież stan',
    },
    refreshTooltip: {
        id: 'src.Screens.Dashboard.Subscription.PaymentHistory.refreshTooltip',
        defaultMessage: 'Odświeża stan subskrypcji po opłaceniu faktury, jeśli aplikacja nadal pokazuje nieuregulowaną płatność.',
    },
    refreshDisabledTooltip: {
        id: 'src.Screens.Dashboard.Subscription.PaymentHistory.refreshDisabledTooltip',
        defaultMessage: 'Odświeżenie jest dostępne tylko wtedy, gdy aplikacja wykrywa nieuregulowaną płatność.',
    },
    refreshCooldownTooltip: {
        id: 'src.Screens.Dashboard.Subscription.PaymentHistory.refreshCooldownTooltip',
        defaultMessage: 'Odświeżanie można wykonać raz na sekundę. Spróbuj ponownie za chwilę.',
    },
    invoiceNumber: {
        id: 'src.Screens.Dashboard.Subscription.PaymentHistory.invoiceNumber',
        defaultMessage: 'Numer',
    },
    paymentDate: {
        id: 'src.Screens.Dashboard.Subscription.PaymentHistory.paymentDate',
        defaultMessage: 'Data płatności',
    },
    amount: {
        id: 'src.Screens.Dashboard.Subscription.PaymentHistory.amount',
        defaultMessage: 'Kwota',
    },
    status: {
        id: 'src.Screens.Dashboard.Subscription.PaymentHistory.status',
        defaultMessage: 'Status',
    },
    invoice: {
        id: 'src.Screens.Dashboard.Subscription.PaymentHistory.invoice',
        defaultMessage: 'Faktura',
    },
    downloadPdf: {
        id: 'src.Screens.Dashboard.Subscription.PaymentHistory.downloadPdf',
        defaultMessage: 'Pobierz PDF',
    },
    checkInvoice: {
        id: 'src.Screens.Dashboard.Subscription.PaymentHistory.checkInvoice',
        defaultMessage: 'Sprawdź w fakturze',
    },
    emptyState: {
        id: 'src.Screens.Dashboard.Subscription.PaymentHistory.emptyState',
        defaultMessage: 'Brak historii płatności',
    },
});
